import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageBarActions, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import {
  CheckmarkCircle20Regular,
  DismissCircle20Regular,
  DocumentText16Regular,
  Open16Regular,
  PersonAdd20Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideDrawer, GuideDrawerAction, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideBannerAction, GuideLinkButton } from '../../../theme/components/GuideButtons';
import { StatusPill } from '../../../theme/components/StatusPill';
import { StatusTone } from '../../../theme/guideTokens';
import { fmtNumber } from '../../Protocols/ConsumptionDetails/ConsumptionDetails.Format';
// Estado como círculo: misma función y mismo CSS que la primera columna de la grilla (US6), sin colores nuevos.
import { GetStatusDescription } from '../../Protocols/ConsumptionList/ConsumptionList.Functions';
import '../../Protocols/ConsumptionList/ConsumptionList.Styles.css';
// Hechos del resumen con el mismo patrón visual que el detalle de consumo de protocolo (cdt-facts).
import '../../Protocols/ConsumptionDetails/ConsumptionDetails.Styles.css';

import {
  alarmReasonLabelKey,
  appliedPriceTypeLabelKey,
  consumptionSettlementLink,
  consumptionStatusLabelKey,
  formatDateTime,
  formatMoney,
  formatPercentage,
  hasCurrencyConversion,
  isProviderFileImport,
  originLabelKey,
  providerAssignmentStatus,
  providerConsumptionDetailActions,
  rowProtocolLabel,
  valuationSourceLabelKey,
} from '../../../functions/ProviderConsumptions.Functions';
import { confidencePercent, formatImportDate, getApiErrorMessage, isLowConfidence } from '../../../functions/ProviderConsumptionImport.Functions';
import {
  ProviderConsumption,
  ProviderToSettleRequest,
  ProviderToSettleResult,
} from '../../../models/Entities/ProviderConsumptions/ProviderConsumptions';
import { ImportAssignmentStatus } from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';

const ASSIGNMENT_TONE: Record<ImportAssignmentStatus, StatusTone> = {
  Unassigned: 'neutral',
  Assigned: 'info',
  Generated: 'success',
};

export interface ProviderConsumptionDetailsDrawerProps {
  /** Consumo de la fila (se muestra al instante); undefined = cerrado. */
  item: ProviderConsumption | undefined;
  /** Falso mientras otro drawer (p. ej. la asignación) está encima: se oculta sin perder el consumo. */
  open: boolean;
  providerName?: string;
  /** Nombre del archivo de importación por id. */
  fileNameById: Map<string, string>;
  /** Cambia después de una acción hecha fuera del drawer (asignación): vuelve a pedir el consumo. */
  refreshKey?: number;
  /** GET protocols/providers/consumptions/{id}. */
  LoadConsumption: (id: string) => Promise<ProviderConsumption | undefined>;
  /** PATCH protocols/providers/consumptions/to-settle (thunk existente de la sección). */
  MarkToSettle: (request: ProviderToSettleRequest) => Promise<ProviderToSettleResult>;
  /** Recarga grilla, KPI y barra "Por liquidar" después de una acción del drawer. */
  onChanged: () => void;
  onAssign: (item: ProviderConsumption) => void;
  onOpenSettlement: (settlementId: number) => void;
  onOpenProtocolConsumption: (item: ProviderConsumption) => void;
  onDismiss: () => void;
}

type ActionNotice =
  | { kind: 'done'; toSettle: boolean }
  | { kind: 'rejected'; message: string }
  | { kind: 'error'; error: unknown; toSettle: boolean };

const hasValue = (value: unknown): boolean => value !== null && value !== undefined && String(value).trim() !== '';

/** Hecho del resumen: etiqueta arriba, valor abajo (patrón cdt-fact). */
const Fact = ({ label, children, wide, testId }: { label: string; children: React.ReactNode; wide?: boolean; testId?: string }) => (
  <div className={wide ? 'cdt-fact cdt-fact--wide' : 'cdt-fact'} data-testid={testId}>
    <span className="cdt-fact__label">{label}</span>
    <span className="cdt-fact__value">{children}</span>
  </div>
);

/**
 * Detalle de un consumo de proveedor (spec 010, US8): drawer de sólo lectura que muestra al instante los datos de la
 * fila y los refresca con el GET por id; secciones de protocolo y paciente, servicio, importes, alarmas, importación
 * (sólo origen 5) y vínculos; acciones del pie según el estado, con los handlers de la sección.
 */
export const ProviderConsumptionDetailsDrawer = (props: ProviderConsumptionDetailsDrawerProps) => {
  const { t } = useTranslation();
  const tr = (path: string, options?: Record<string, unknown>) => t(`providerConsumptions.detail.${path}`, options);
  const { item, LoadConsumption } = props;
  const id = item?.id;

  const [consumption, setConsumption] = useState<ProviderConsumption | undefined>(item);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(undefined);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState<'mark' | 'unmark' | undefined>(undefined);
  const [notice, setNotice] = useState<ActionNotice | undefined>(undefined);
  const request = useRef(0);

  // Otro consumo: se muestran los datos de su fila y se limpian los avisos.
  useEffect(() => {
    setConsumption(item);
    setLoadError(undefined);
    setNotice(undefined);
    setBusy(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const load = useCallback(async () => {
    if (!id) return;
    const requestId = ++request.current;
    setLoading(true);
    setLoadError(undefined);
    try {
      const fresh = await LoadConsumption(id);
      if (requestId !== request.current) return;
      if (fresh) setConsumption((current) => ({ ...(current ?? {}), ...fresh, id: fresh.id || id }));
    } catch (error: unknown) {
      if (requestId === request.current) setLoadError(error);
    } finally {
      if (requestId === request.current) setLoading(false);
    }
  }, [id, LoadConsumption]);

  useEffect(() => {
    if (props.open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, props.open, props.refreshKey, reload]);

  // Al cerrar se descartan las respuestas pendientes.
  useEffect(() => {
    if (!item) request.current += 1;
  }, [item]);

  if (!consumption) return null;
  const c = consumption;

  const runToSettle = async (toSettle: boolean) => {
    setBusy(toSettle ? 'mark' : 'unmark');
    setNotice(undefined);
    try {
      const result = await props.MarkToSettle({ ids: [c.id], toSettle });
      if (result.updated > 0) {
        setNotice({ kind: 'done', toSettle });
      } else {
        const message = result.rejected?.find((rejection) => rejection.id === c.id)?.message || result.rejected?.[0]?.message;
        setNotice({ kind: 'rejected', message: message?.trim() || tr('result.rejectedGeneric') });
      }
      props.onChanged();
      setReload((current) => current + 1);
    } catch (error: unknown) {
      setNotice({ kind: 'error', error, toSettle });
    } finally {
      setBusy(undefined);
    }
  };

  const statusLabel = t(consumptionStatusLabelKey(c.status));
  const statusNumber = c.status === null || c.status === undefined ? Number.NaN : Number(c.status);
  const service = c.serviceName || c.medicalServiceName || tr('title');
  const actions = providerConsumptionDetailActions(c);
  const settlement = consumptionSettlementLink(c);
  const assignment = providerAssignmentStatus(c);
  const isImport = isProviderFileImport(c);
  const conversion = hasCurrencyConversion(c);
  const hasAlarmReason = hasValue(c.settlementAlarmReason) || hasValue(c.settlementAlarmReasonDescription);
  const hasWarnings = !!c.markedAsHaveWarnings || hasValue(c.warningsComments);
  const fileId = hasValue(c.providerFileId) ? String(c.providerFileId) : undefined;
  const fileName = fileId ? props.fileNameById.get(fileId) ?? fileId : undefined;
  const quantity = hasValue(c.quantity) ? Number(c.quantity) : undefined;
  const settlementText = settlement
    ? settlement.label
      ? t('providerConsumptions.grid.settlementLink', { name: settlement.label })
      : t('providerConsumptions.grid.settlementLinkFallback', { id: settlement.settlementId })
    : undefined;

  // ---- pie: la acción de estado es la principal; el resto, secundarias ----
  const stateAction: GuideDrawerAction | undefined = actions.mark
    ? {
        label: busy === 'mark' ? tr('actions.marking') : tr('actions.mark'),
        onClick: () => runToSettle(true),
        loading: busy === 'mark',
        disabled: !!busy,
        icon: <CheckmarkCircle20Regular />,
        testId: 'pcd-action-mark',
      }
    : actions.unmark
      ? {
          label: busy === 'unmark' ? tr('actions.unmarking') : tr('actions.unmark'),
          onClick: () => runToSettle(false),
          loading: busy === 'unmark',
          disabled: !!busy,
          icon: <DismissCircle20Regular />,
          testId: 'pcd-action-unmark',
        }
      : undefined;
  const extraActions: GuideDrawerAction[] = [
    actions.assign && {
      label: tr('actions.assign'),
      onClick: () => props.onAssign(c),
      disabled: !!busy,
      icon: <PersonAdd20Regular />,
      testId: 'pcd-action-assign',
    },
    actions.settlement && settlement && {
      label: tr('actions.viewSettlement'),
      onClick: () => props.onOpenSettlement(settlement.settlementId),
      icon: <DocumentText16Regular />,
      testId: 'pcd-action-settlement',
    },
    actions.protocolConsumption && {
      label: tr('actions.viewProtocolConsumption'),
      onClick: () => props.onOpenProtocolConsumption(c),
      icon: <Open16Regular />,
      testId: 'pcd-action-protocol-consumption',
    },
  ].filter(Boolean) as GuideDrawerAction[];

  const eyebrow = [props.providerName, t(originLabelKey(c.origin))].filter(Boolean).join(' · ');

  const renderNotices = () => (
    <>
      {loading && (
        <div className="pcd-refreshing" role="status" aria-live="polite" data-testid="pcd-refreshing">
          <Spinner size="extra-tiny" />
          <span>{tr('refreshing')}</span>
        </div>
      )}
      {loadError !== undefined && (
        <GuideMessageBar intent="warning" layout="multiline" data-testid="pcd-load-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadError')}</MessageBarTitle>
            {getApiErrorMessage(loadError)} {tr('loadErrorHint')}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => setReload((current) => current + 1)} data-testid="pcd-load-retry">
              {tr('retry')}
            </GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
      {notice?.kind === 'done' && (
        <GuideMessageBar intent="success" data-testid="pcd-action-done">
          <MessageBarBody>{tr(notice.toSettle ? 'result.marked' : 'result.unmarked')}</MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => setNotice(undefined)}>{tr('result.dismiss')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
      {notice?.kind === 'rejected' && (
        <GuideMessageBar intent="warning" layout="multiline" data-testid="pcd-action-rejected">
          <MessageBarBody>
            <MessageBarTitle>{tr('result.rejectedTitle')}</MessageBarTitle>
            {notice.message}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => setNotice(undefined)}>{tr('result.dismiss')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
      {notice?.kind === 'error' && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pcd-action-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('result.errorTitle')}</MessageBarTitle>
            {getApiErrorMessage(notice.error)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => runToSettle(notice.toSettle)} disabled={!!busy} data-testid="pcd-action-retry">
              {tr('retry')}
            </GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
    </>
  );

  const renderAlarms = () => {
    if (!hasAlarmReason && !hasWarnings) {
      return (
        <StatusPill tone="success" symbol="✓" data-testid="pcd-no-alarms">
          {tr('alarm.none')}
        </StatusPill>
      );
    }
    const description = c.settlementAlarmReasonDescription?.trim() || (hasValue(c.settlementAlarmReason) ? t(alarmReasonLabelKey(c.settlementAlarmReason)) : '');
    const detail = c.settlementAlarmDetail?.trim();
    return (
      <div className="pcd-alarms">
        {hasAlarmReason && (
          <GuideMessageBar intent="warning" layout="multiline" data-testid="pcd-alarm">
            <MessageBarBody>
              <MessageBarTitle>{tr('alarm.reason')}</MessageBarTitle>
              <span className="pcd-alarm__text">{description}</span>
              {detail && detail !== description && <span className="pcd-alarm__detail">{detail}</span>}
            </MessageBarBody>
          </GuideMessageBar>
        )}
        {hasWarnings && (
          <GuideMessageBar intent="warning" layout="multiline" data-testid="pcd-warnings">
            <MessageBarBody>
              <MessageBarTitle>{tr('alarm.warnings')}</MessageBarTitle>
              <span className="pcd-alarm__text">{c.warningsComments?.trim() || tr('alarm.warningsNoComment')}</span>
            </MessageBarBody>
          </GuideMessageBar>
        )}
      </div>
    );
  };

  return (
    <GuideDrawer
      open={props.open}
      onDismiss={props.onDismiss}
      size="medium"
      eyebrow={eyebrow || tr('eyebrow')}
      title={<span className="cdt-title">{service}</span>}
      subtitle={
        <span className="pcd-status" data-testid="pcd-status">
          <span className={`status-mark ${GetStatusDescription(statusNumber).class}`} role="img" aria-label={statusLabel} data-testid="pcd-status-mark" />
          <span className="pcd-status__label">{statusLabel}</span>
        </span>
      }
      closeLabel={tr('close')}
      primaryAction={stateAction}
      extraActions={extraActions}
      secondaryAction={{ label: tr('close'), testId: 'pcd-close' }}
      className="pcd-drawer"
      testId="pcd-drawer"
    >
      <div className="cdt pcd" data-testid="pcd-root" aria-busy={loading || undefined}>
        {renderNotices()}

        <GuideDrawerSection title={tr('sections.protocol')} testId="pcd-section-protocol">
          <div className="cdt-facts">
            <Fact label={tr('fields.protocol')} testId="pcd-protocol">
              {rowProtocolLabel(c) || <span className="pcd-muted">{tr('values.noProtocol')}</span>}
            </Fact>
            {hasValue(c.stageName) && <Fact label={tr('fields.stage')}>{c.stageName}</Fact>}
            <Fact label={tr('fields.patient')} testId="pcd-patient">
              {c.patientNumberInProtocol || <span className="pcd-muted">{tr('values.noPatient')}</span>}
            </Fact>
            {hasValue(c.medicalRecordNumber) && <Fact label={tr('fields.medicalRecord')}>{c.medicalRecordNumber}</Fact>}
            {hasValue(c.patientReference) && (
              <Fact label={tr('fields.patientReference')} testId="pcd-patient-reference">
                {c.patientReference}
              </Fact>
            )}
            {assignment && (
              <Fact label={tr('fields.assignment')} testId="pcd-assignment">
                <StatusPill tone={ASSIGNMENT_TONE[assignment]}>{t(`providerConsumptionImport.status.${assignment}`)}</StatusPill>
              </Fact>
            )}
          </div>
        </GuideDrawerSection>

        <GuideDrawerSection title={tr('sections.service')} testId="pcd-section-service">
          <div className="cdt-facts">
            {hasValue(c.medicalServiceCode) && (
              <Fact label={tr('fields.medicalServiceCode')}>
                <span className="cdt-fact__value--num">{c.medicalServiceCode}</span>
              </Fact>
            )}
            {hasValue(c.medicalServiceName) && <Fact label={tr('fields.medicalServiceName')}>{c.medicalServiceName}</Fact>}
            {hasValue(c.serviceName) && <Fact label={tr('fields.serviceName')}>{c.serviceName}</Fact>}
            {(hasValue(c.conceptDescription) || hasValue(c.contractConceptId)) && (
              <Fact label={tr('fields.concept')}>{c.conceptDescription || `#${c.contractConceptId}`}</Fact>
            )}
            <Fact label={tr('fields.serviceDate')}>
              <span className="cdt-fact__value--num">{formatImportDate(c.serviceDueDate)}</span>
            </Fact>
            {quantity !== undefined && (
              <Fact label={tr('fields.quantity')} testId="pcd-quantity">
                <span className="cdt-fact__value--num">{fmtNumber(quantity, 0, 3)}</span>
              </Fact>
            )}
          </div>
        </GuideDrawerSection>

        <GuideDrawerSection title={tr('sections.amounts')} testId="pcd-section-amounts">
          <div className="cdt-facts">
            {hasValue(c.unitAmount) && (
              <Fact label={tr('fields.unitAmount')}>
                <span className="cdt-fact__value--num">{formatMoney(c.unitAmount, c.currencyCode)}</span>
              </Fact>
            )}
            <Fact label={tr('fields.amount')} testId="pcd-amount">
              <span className="cdt-fact__value--num pcd-amount">{formatMoney(c.amount, c.currencyCode)}</span>
            </Fact>
            {hasValue(c.currencyCode) && <Fact label={tr('fields.currency')}>{c.currencyCode}</Fact>}
            {conversion && (
              <>
                <Fact label={tr('fields.originalCurrency')} testId="pcd-original-currency">
                  {c.originalCurrencyCode}
                </Fact>
                {hasValue(c.originalUnitAmount) && (
                  <Fact label={tr('fields.originalUnitAmount')}>
                    <span className="cdt-fact__value--num">{formatMoney(c.originalUnitAmount, c.originalCurrencyCode)}</span>
                  </Fact>
                )}
                {hasValue(c.originalAmount) && (
                  <Fact label={tr('fields.originalAmount')}>
                    <span className="cdt-fact__value--num">{formatMoney(c.originalAmount, c.originalCurrencyCode)}</span>
                  </Fact>
                )}
                {hasValue(c.rate) && (
                  <Fact label={tr('fields.rate')} testId="pcd-rate">
                    <span className="cdt-fact__value--num">{fmtNumber(Number(c.rate), 2, 6)}</span>
                  </Fact>
                )}
              </>
            )}
            {Number(c.payeeAmount ?? 0) > 0 && (
              <Fact label={tr('fields.payeeAmount')}>
                <span className="cdt-fact__value--num">{formatMoney(c.payeeAmount, c.currencyCode)}</span>
              </Fact>
            )}
            {Number(c.pendingAmount ?? 0) > 0 && (
              <Fact label={tr('fields.pendingAmount')}>
                <span className="cdt-fact__value--num">{formatMoney(c.pendingAmount, c.currencyCode)}</span>
              </Fact>
            )}
            <Fact label={tr('fields.valuationSource')} testId="pcd-valuation-source">
              {t(valuationSourceLabelKey(c.costValuationSource))}
            </Fact>
            {hasValue(c.priceListId) && (
              <Fact label={tr('fields.priceList')} testId="pcd-price-list">
                {tr('values.priceListId', { id: c.priceListId })}
              </Fact>
            )}
            {hasValue(c.priceListEffectiveDateStart) && (
              <Fact label={tr('fields.priceListEffective')}>
                {tr('values.effective', {
                  from: formatImportDate(c.priceListEffectiveDateStart),
                  to: c.priceListEffectiveDateEnd ? formatImportDate(c.priceListEffectiveDateEnd) : t('providerConsumptions.valuation.noEndDate'),
                })}
              </Fact>
            )}
            {hasValue(c.appliedPriceType) && <Fact label={tr('fields.appliedPriceType')}>{t(appliedPriceTypeLabelKey(c.appliedPriceType))}</Fact>}
            {hasValue(c.costPercentage) && (
              <Fact label={tr('fields.costPercentage')} testId="pcd-cost-percentage">
                {tr('values.percentage', { value: formatPercentage(c.costPercentage) })}
              </Fact>
            )}
            {hasValue(c.costBaseUnitAmount) && (
              <Fact label={tr('fields.costBase')}>
                <span className="cdt-fact__value--num">{formatMoney(c.costBaseUnitAmount, c.currencyCode)}</span>
              </Fact>
            )}
          </div>
        </GuideDrawerSection>

        <GuideDrawerSection title={tr('sections.alarms')} testId="pcd-section-alarms">
          {renderAlarms()}
        </GuideDrawerSection>

        {isImport && (
          <GuideDrawerSection title={tr('sections.import')} testId="pcd-section-import">
            <div className="cdt-facts">
              {fileName && (
                <Fact label={tr('fields.file')} wide testId="pcd-file">
                  {fileName}
                </Fact>
              )}
              {hasValue(c.sourceRowNumber) && (
                <Fact label={tr('fields.sourceRow')}>
                  <span className="cdt-fact__value--num">{c.sourceRowNumber}</span>
                </Fact>
              )}
              {hasValue(c.sourceReference) && (
                <Fact label={tr('fields.sourceReference')} testId="pcd-source-reference">
                  {c.sourceReference}
                </Fact>
              )}
              {hasValue(c.importConfidence) && (
                <Fact label={tr('fields.confidence')} testId="pcd-confidence">
                  <span className="pcd-confidence">
                    <span className="cdt-fact__value--num">{tr('values.confidence', { value: confidencePercent(c.importConfidence) })}</span>
                    {isLowConfidence(c.importConfidence) && (
                      <StatusPill tone="warning" symbol="!" data-testid="pcd-low-confidence">
                        {tr('values.lowConfidence')}
                      </StatusPill>
                    )}
                  </span>
                </Fact>
              )}
              <Fact label={tr('fields.importAmount')} testId="pcd-import-amount">
                {hasValue(c.importAmount) ? (
                  <span className="cdt-fact__value--num">{formatMoney(c.importAmount, c.importCurrencyCode || c.currencyCode)}</span>
                ) : (
                  <span className="pcd-muted">{tr('fields.importNoValue')}</span>
                )}
              </Fact>
              {hasValue(c.assignedAt) && <Fact label={tr('fields.assignedAt')}>{formatDateTime(c.assignedAt)}</Fact>}
              {hasValue(c.generatedAt) && <Fact label={tr('fields.generatedAt')}>{formatDateTime(c.generatedAt)}</Fact>}
            </div>
          </GuideDrawerSection>
        )}

        <GuideDrawerSection title={tr('sections.links')} testId="pcd-section-links">
          {settlement || actions.protocolConsumption ? (
            <div className="cdt-facts">
              {settlement && (
                <Fact label={tr('fields.settlement')} testId="pcd-settlement">
                  <GuideLinkButton
                    size="small"
                    icon={<DocumentText16Regular />}
                    aria-label={t('providerConsumptions.grid.settlementLinkLabel', { name: settlement.label ?? `#${settlement.settlementId}` })}
                    onClick={() => props.onOpenSettlement(settlement.settlementId)}
                    data-testid="pcd-settlement-link"
                  >
                    {settlementText}
                  </GuideLinkButton>
                </Fact>
              )}
              {actions.protocolConsumption && (
                <Fact label={tr('fields.protocolConsumption')} testId="pcd-protocol-consumption">
                  <GuideLinkButton
                    size="small"
                    icon={<Open16Regular />}
                    title={tr('links.protocolConsumptionHint')}
                    onClick={() => props.onOpenProtocolConsumption(c)}
                    data-testid="pcd-protocol-consumption-link"
                  >
                    {tr('actions.viewProtocolConsumption')}
                  </GuideLinkButton>
                </Fact>
              )}
            </div>
          ) : (
            <span className="pcd-muted" data-testid="pcd-no-links">
              {tr('links.none')}
            </span>
          )}
        </GuideDrawerSection>
      </div>
    </GuideDrawer>
  );
};

export default ProviderConsumptionDetailsDrawer;
